/**
 * Admin — Manutenção Global de Formadores (mestre).
 *
 * Lista consolidada de todos os formadores da entidade (biblioteca central).
 * CRUD completo + view detalhado com CV, habilitações e histórico de cursos.
 *
 * URL: /admin/formadores
 */
import { useCallback, useEffect, useState } from "react";
import {
  UserCog, Search, Plus, Loader2, Pencil, Trash2, X,
  ExternalLink, BookOpen, Award, Save, RefreshCcw, CheckCircle2,
  Upload, FileText,
} from "lucide-react";
import { adminApi } from "../api";
import { toast } from "sonner";
import { verifyEmail } from "@/shared/emailValidation";

export default function AdminFormadores() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState(null); // full record or "new"
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { q };
      if (!showInactive) params.activo = "S";
      const r = await adminApi.formadoresList(params);
      setRows(r.formadores || []);
    } catch (e) { toast.error(e.message || "Erro a carregar"); }
    finally { setLoading(false); }
  }, [q, showInactive]);

  useEffect(() => {
    const t = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(t);
  }, [load]);

  const openEdit = async (fid) => {
    try {
      const full = await adminApi.formadorGet(fid);
      setEditing(full);
    } catch (e) { toast.error(e.message || "Erro a carregar formador"); }
  };

  const onDelete = async (f) => {
    if (!window.confirm(`Desactivar o formador "${f.nome}"?`)) return;
    try {
      await adminApi.formadorDelete(f.id);
      toast.success("Formador desactivado");
      await load();
    } catch (e) { toast.error(e.message || "Erro"); }
  };

  const onSave = async (data) => {
    setSaving(true);
    try {
      if (editing?.id) {
        await adminApi.formadorUpdate(editing.id, data);
        toast.success("Formador actualizado");
      } else {
        await adminApi.formadorCreate(data);
        toast.success("Formador criado");
      }
      setEditing(null);
      await load();
    } catch (e) { toast.error(e.message || "Erro a gravar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="admin-formadores-page">
      <header className="mb-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#F3F0E7] grid place-items-center">
          <UserCog className="w-5 h-5 text-[#A8843C]" />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-[#1B1B1B]">Manutenção de Formadores</h1>
          <p className="text-sm text-[#6E6754]">
            Biblioteca central dos formadores da entidade — habilitações, CV, contactos e cursos onde participam.
          </p>
        </div>
        <button
          onClick={() => setEditing({ activo: true })}
          data-testid="btn-new-formador"
          className="text-xs px-3 py-1.5 bg-[#A8843C] text-white rounded hover:bg-[#8f6e2f] inline-flex items-center gap-1.5"
        >
          <Plus size={12} /> Novo formador
        </button>
      </header>

      <div className="bg-white border border-[#E6E1D6] rounded-md p-3 mb-4 flex items-center gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#978D78]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Procurar por nome, email ou NIF…"
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-[#E6E1D6] rounded focus:border-[#A8843C] focus:outline-none"
            data-testid="input-search"
          />
        </div>
        <label className="text-xs flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={showInactive}
                 onChange={(e) => setShowInactive(e.target.checked)}
                 data-testid="chk-show-inactive" />
          Incluir inactivos
        </label>
        <button onClick={load} className="text-xs text-[#6B7280] hover:text-[#0F1829] inline-flex items-center gap-1"
                data-testid="btn-reload">
          <RefreshCcw size={12} /> Recarregar
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-6 text-[#6E6754]">
          <Loader2 className="w-4 h-4 animate-spin" /> A carregar…
        </div>
      ) : rows.length === 0 ? (
        <div className="p-8 bg-white border border-[#E6E1D6] rounded-md text-center">
          <p className="text-sm text-[#6E6754] mb-3">Sem formadores{q ? " que correspondam à pesquisa" : ""}.</p>
          <button onClick={() => setEditing({ activo: true })}
                  className="text-xs px-3 py-1.5 bg-[#A8843C] text-white rounded hover:bg-[#8f6e2f] inline-flex items-center gap-1.5">
            <Plus size={12} /> Adicionar primeiro formador
          </button>
        </div>
      ) : (
        <div className="bg-white border border-[#E6E1D6] rounded-md overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-[#FAF8F2] text-left text-[10px] uppercase tracking-wider text-[#978D78]">
              <tr>
                <th className="px-3 py-2 min-w-[200px]">Nome</th>
                <th className="px-3 py-2 min-w-[180px]">Contactos</th>
                <th className="px-3 py-2">NIF</th>
                <th className="px-3 py-2 text-center">Cursos</th>
                <th className="px-3 py-2 text-center">Estado</th>
                <th className="px-3 py-2 text-right w-24">Acções</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((f) => (
                <tr key={f.id} className="border-t border-[#F3F0E7] hover:bg-[#FCFAF3]"
                    data-testid={`row-formador-${f.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-[#1B1B1B]">{f.nome}</div>
                    {f.linkedin && (
                      <a href={f.linkedin} target="_blank" rel="noreferrer"
                         className="text-[10px] text-[#A8843C] hover:underline inline-flex items-center gap-0.5">
                        LinkedIn <ExternalLink size={9} />
                      </a>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-[#6E6754]">
                    {f.email || <span className="italic text-[#B8AC8A]">—</span>}
                    {f.telefone && <div className="text-[10px]">{f.telefone}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs text-[#6E6754]">{f.nif || "—"}</td>
                  <td className="px-3 py-2 text-center">
                    <span className="inline-flex items-center gap-1 text-xs text-[#0F1829] font-semibold">
                      <BookOpen size={11} /> {f.total_cursos}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      f.activo ? "bg-[#E6F4EA] text-[#0A6B2E]" : "bg-[#F3F0E7] text-[#6E6754]"
                    }`}>{f.activo ? "activo" : "inactivo"}</span>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(f.id)}
                            data-testid={`btn-edit-${f.id}`}
                            className="text-[10px] px-2 py-1 rounded bg-white border border-[#E6E1D6] hover:bg-[#FAF8F2] inline-flex items-center gap-1">
                      <Pencil size={10} /> Ver
                    </button>
                    {f.activo && (
                      <button onClick={() => onDelete(f)}
                              data-testid={`btn-del-${f.id}`}
                              className="ml-1 text-[10px] px-2 py-1 rounded bg-white border border-[#FCA5A5] text-[#9B1C1C] hover:bg-[#FDE8E8] inline-flex items-center gap-1">
                        <Trash2 size={10} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {editing !== null && (
        <FormadorModal
          formador={editing}
          onClose={() => setEditing(null)}
          onSave={onSave}
          saving={saving}
        />
      )}
    </div>
  );
}


function FormadorModal({ formador, onClose, onSave, saving }) {
  const isNew = !formador?.id;
  const [f, setF] = useState({
    nome: formador?.nome || "",
    email: formador?.email || "",
    nif: formador?.nif || "",
    telefone: formador?.telefone || "",
    iban: formador?.iban || "",
    linkedin: formador?.linkedin || "",
    habilitacoes: formador?.habilitacoes || "",
    certificacoes: formador?.certificacoes || "",
    experiencia: formador?.experiencia || "",
    notas: formador?.notas || "",
    activo: formador?.activo !== false,
    id_cliente: formador?.id_cliente || null,
  });
  const [cliQ, setCliQ] = useState("");
  const [cliResults, setCliResults] = useState([]);
  const [cliSearching, setCliSearching] = useState(false);
  const [cliOpen, setCliOpen] = useState(false);

  useEffect(() => {
    if (!cliOpen || cliQ.trim().length < 2) { setCliResults([]); return; }
    const t = setTimeout(async () => {
      setCliSearching(true);
      try {
        const r = await adminApi.formadorSearchCliente(cliQ);
        setCliResults(r.results || []);
      } catch { /* silent */ } finally { setCliSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [cliQ, cliOpen]);

  const pickCliente = (c) => {
    setF((prev) => ({
      ...prev,
      id_cliente: c.id_cliente,
      // auto-preenche campos vazios
      email: prev.email || c.email || "",
      nif: prev.nif || c.nif || "",
    }));
    setCliOpen(false); setCliQ(""); setCliResults([]);
    toast.success(`Cliente #${c.id_cliente} associado`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.nome.trim()) { toast.error("Nome obrigatório"); return; }
    if (f.email.trim()) {
      const ev = await verifyEmail(f.email);
      if (!ev.valid) {
        toast.error(ev.suggestion ? `${ev.message} Quis dizer ${ev.suggestion}?` : ev.message);
        return;
      }
    }
    const payload = {
      nome: f.nome.trim(),
      email: f.email.trim() || null,
      nif: f.nif.trim() || null,
      telefone: f.telefone.trim() || null,
      iban: f.iban.trim() || null,
      linkedin: f.linkedin.trim() || null,
      habilitacoes: f.habilitacoes.trim() || null,
      certificacoes: f.certificacoes.trim() || null,
      experiencia: f.experiencia.trim() || null,
      notas: f.notas.trim() || null,
      id_cliente: f.id_cliente || null,
    };
    if (!isNew) payload.activo = f.activo;
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 overflow-y-auto"
         onClick={onClose} data-testid="modal-formador">
      <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full my-8"
           onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit}>
          <header className="p-4 border-b border-[#E6E1D6] flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[#0F1829]">
              {isNew ? "Novo formador" : `Editar: ${formador.nome}`}
            </h3>
            <button type="button" onClick={onClose} className="text-[#6E6754] hover:text-[#0F1829]">
              <X size={16} />
            </button>
          </header>
          <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nome completo *" required>
                <input value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })}
                       className="w-full px-2 py-1.5 text-sm border border-[#E6E1D6] rounded focus:border-[#A8843C] focus:outline-none"
                       data-testid="input-nome" required />
              </Field>
              <Field label="Email">
                <input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
                       className="w-full px-2 py-1.5 text-sm border border-[#E6E1D6] rounded focus:border-[#A8843C] focus:outline-none"
                       data-testid="input-email" />
              </Field>
              <Field label="NIF">
                <input value={f.nif} onChange={(e) => setF({ ...f, nif: e.target.value })}
                       className="w-full px-2 py-1.5 text-sm border border-[#E6E1D6] rounded focus:border-[#A8843C] focus:outline-none"
                       data-testid="input-nif" />
              </Field>
              <Field label="Telefone">
                <input value={f.telefone} onChange={(e) => setF({ ...f, telefone: e.target.value })}
                       className="w-full px-2 py-1.5 text-sm border border-[#E6E1D6] rounded focus:border-[#A8843C] focus:outline-none"
                       data-testid="input-tel" />
              </Field>
              <Field label="IBAN (para pagamentos)">
                <input value={f.iban} onChange={(e) => setF({ ...f, iban: e.target.value })}
                       placeholder="PT50 ..."
                       className="w-full px-2 py-1.5 text-sm border border-[#E6E1D6] rounded focus:border-[#A8843C] focus:outline-none font-mono text-xs"
                       data-testid="input-iban" />
              </Field>
              <Field label="LinkedIn">
                <input type="url" value={f.linkedin} onChange={(e) => setF({ ...f, linkedin: e.target.value })}
                       placeholder="https://linkedin.com/in/…"
                       className="w-full px-2 py-1.5 text-xs border border-[#E6E1D6] rounded focus:border-[#A8843C] focus:outline-none"
                       data-testid="input-linkedin" />
              </Field>
            </div>

            {/* Ligação a Cliente (Tinf_Cliente) */}
            <div className="pt-3 border-t border-[#F3F0E7]">
              <p className="text-[11px] uppercase tracking-wider text-[#978D78] font-bold mb-2">
                Ligação a Cliente (Tinf_Cliente)
              </p>
              {f.id_cliente ? (
                <div className="flex items-center gap-2 p-2 bg-[#E6F4EA] border border-[#A6DFB6] rounded text-xs">
                  <CheckCircle2 size={12} className="text-[#0A6B2E]" />
                  <span className="text-[#0A6B2E] font-semibold">
                    Cliente #{f.id_cliente} associado
                  </span>
                  <button type="button" onClick={() => setF({ ...f, id_cliente: null })}
                          className="ml-auto text-[10px] px-2 py-1 rounded border border-[#0A6B2E] text-[#0A6B2E] hover:bg-white"
                          data-testid="btn-unlink-cliente">
                    Remover
                  </button>
                </div>
              ) : cliOpen ? (
                <div className="space-y-2">
                  <div className="relative">
                    <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#978D78]" />
                    <input value={cliQ} onChange={(e) => setCliQ(e.target.value)}
                           placeholder="Nome, email, NIF ou ID_Cliente…"
                           className="w-full pl-7 pr-2 py-1.5 text-xs border border-[#E6E1D6] rounded focus:border-[#A8843C] focus:outline-none"
                           autoFocus data-testid="input-search-cliente" />
                  </div>
                  <ul className="max-h-40 overflow-y-auto space-y-1">
                    {cliSearching && (
                      <li className="text-xs text-[#6E6754] flex items-center gap-2 p-2">
                        <Loader2 className="animate-spin" size={12} /> A pesquisar…
                      </li>
                    )}
                    {!cliSearching && cliResults.length === 0 && cliQ.trim().length >= 2 && (
                      <li className="text-xs text-[#978D78] italic p-2">Sem resultados.</li>
                    )}
                    {cliResults.map((c) => (
                      <li key={c.id_cliente}>
                        <button type="button" onClick={() => pickCliente(c)}
                                className="w-full text-left p-2 text-xs border border-[#E6E1D6] rounded hover:bg-[#FAF8F2]"
                                data-testid={`res-cli-${c.id_cliente}`}>
                          <span className="font-mono text-[10px] text-[#978D78]">#{c.id_cliente}</span>
                          {" "}<span className="font-semibold text-[#0F1829]">{c.nome}</span>
                          <p className="text-[10px] text-[#978D78]">
                            {c.email || "—"} {c.nif && <>· NIF: {c.nif}</>}
                          </p>
                        </button>
                      </li>
                    ))}
                  </ul>
                  <button type="button" onClick={() => setCliOpen(false)}
                          className="text-[10px] text-[#6B7280] hover:text-[#0F1829]">
                    Cancelar
                  </button>
                </div>
              ) : (
                <button type="button" onClick={() => setCliOpen(true)}
                        className="text-xs px-3 py-1.5 rounded border border-[#E6E1D6] hover:bg-[#FAF8F2] inline-flex items-center gap-1.5"
                        data-testid="btn-link-cliente">
                  <Search size={12} /> Procurar cliente por nome ou ID
                </button>
              )}
            </div>

            <Field label="Habilitações Académicas">
              <textarea value={f.habilitacoes} onChange={(e) => setF({ ...f, habilitacoes: e.target.value })}
                        rows={3}
                        placeholder="Ex.: Licenciatura em Gestão · Mestrado em Coaching · ..."
                        className="w-full px-2 py-1.5 text-sm border border-[#E6E1D6] rounded focus:border-[#A8843C] focus:outline-none resize-y"
                        data-testid="input-habilitacoes" />
            </Field>
            <Field label="Certificações / CCP">
              <textarea value={f.certificacoes} onChange={(e) => setF({ ...f, certificacoes: e.target.value })}
                        rows={2}
                        placeholder="CCP DGERT · Certificado PNL · ..."
                        className="w-full px-2 py-1.5 text-sm border border-[#E6E1D6] rounded focus:border-[#A8843C] focus:outline-none resize-y"
                        data-testid="input-certificacoes" />
            </Field>
            <Field label="Experiência Profissional">
              <textarea value={f.experiencia} onChange={(e) => setF({ ...f, experiencia: e.target.value })}
                        rows={3}
                        className="w-full px-2 py-1.5 text-sm border border-[#E6E1D6] rounded focus:border-[#A8843C] focus:outline-none resize-y"
                        data-testid="input-experiencia" />
            </Field>
            <Field label="Notas internas (uso admin)">
              <textarea value={f.notas} onChange={(e) => setF({ ...f, notas: e.target.value })}
                        rows={2}
                        className="w-full px-2 py-1.5 text-sm border border-[#E6E1D6] rounded focus:border-[#A8843C] focus:outline-none resize-y"
                        data-testid="input-notas" />
            </Field>

            {!isNew && (
              <FormadorDocsSection
                formadorId={formador.id}
                docs={formador.docs || {}}
                onChange={(newDocs) => {
                  // atualiza estado local para reflectir upload/delete sem refresh
                  formador.docs = { ...(formador.docs || {}), ...newDocs };
                  setF({ ...f });
                }}
              />
            )}

            {!isNew && (
              <label className="text-xs flex items-center gap-1.5 cursor-pointer pt-2 border-t border-[#F3F0E7]">
                <input type="checkbox" checked={f.activo}
                       onChange={(e) => setF({ ...f, activo: e.target.checked })}
                       data-testid="chk-activo" />
                Formador activo (desmarcar = soft delete)
              </label>
            )}

            {!isNew && formador?.cursos?.length > 0 && (
              <div className="pt-3 border-t border-[#F3F0E7]">
                <p className="text-[11px] uppercase tracking-wider text-[#978D78] font-bold mb-2">
                  <Award className="inline w-3 h-3 mr-1" />
                  Cursos onde participa ({formador.cursos.length})
                </p>
                <ul className="space-y-1 text-xs max-h-40 overflow-y-auto">
                  {formador.cursos.map((c) => (
                    <li key={c.id_curso} className="p-1.5 bg-[#FAF8F2] rounded flex items-center gap-2">
                      <span className="text-[10px] text-[#978D78] font-mono">#{c.id_curso}</span>
                      <span className="flex-1 truncate">{c.nome}</span>
                      {c.data_inicio && (
                        <span className="text-[10px] text-[#978D78]">
                          {new Date(c.data_inicio).toLocaleDateString("pt-PT")}
                        </span>
                      )}
                      <a href={`/admin/cursos/formadores?curso=${c.id_curso}`}
                         className="text-[10px] text-[#A8843C] hover:underline inline-flex items-center gap-0.5">
                        abrir <ExternalLink size={9} />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <footer className="p-4 border-t border-[#E6E1D6] flex justify-end gap-2">
            <button type="button" onClick={onClose}
                    className="text-xs px-3 py-1.5 rounded border border-[#E6E1D6] hover:bg-[#FAF8F2]">
              Cancelar
            </button>
            <button type="submit" disabled={saving || !f.nome.trim()}
                    className="text-xs px-3 py-1.5 rounded bg-[#A8843C] text-white hover:bg-[#8f6e2f] disabled:opacity-50 inline-flex items-center gap-1.5"
                    data-testid="btn-save-modal">
              {saving ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />}
              {isNew ? "Criar" : "Guardar"}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="text-xs block">
      <span className="block text-[#6E6754] font-semibold mb-1">{label}</span>
      {children}
    </label>
  );
}

/**
 * Secção de documentos anexados ao formador (CCP, Seguro, Ficha de Formador).
 * Cada doc aceita PDF via upload directo. Se já existir, mostra link + botão
 * remover. Endpoints: POST /api/admin/formadores/{fid}/docs/{tipo}/upload,
 * DELETE /api/admin/formadores/{fid}/docs/{tipo}.
 */
const DOC_TYPES = [
  { key: "ccp",            label: "CCP",                   hint: "Certificado de Competências Pedagógicas (PDF)" },
  { key: "seguro",         label: "Seguro de formador",    hint: "Apólice em vigor (PDF)" },
  { key: "ficha_formador", label: "Ficha de formador",     hint: "Documento identificativo assinado (PDF)" },
];

function FormadorDocsSection({ formadorId, docs, onChange }) {
  const [busy, setBusy] = useState(null);

  const upload = async (tipo, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Só é aceite PDF.");
      return;
    }
    setBusy(tipo);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`/api/admin/formadores/${formadorId}/docs/${tipo}/upload`, {
        method: "POST", credentials: "include", body: fd,
      });
      if (!res.ok) throw new Error(await res.text());
      const j = await res.json();
      toast.success(`${DOC_TYPES.find((d) => d.key === tipo)?.label} carregado.`);
      onChange({ [tipo]: { path: j.path, url: j.url, uploaded_em: new Date().toISOString() } });
    } catch (e) {
      toast.error(`Upload falhou: ${e.message || e}`);
    } finally { setBusy(null); }
  };
  const remove = async (tipo) => {
    if (!window.confirm(`Remover ${DOC_TYPES.find((d) => d.key === tipo)?.label}? O ficheiro CDN mantém-se.`))
      return;
    setBusy(tipo);
    try {
      const res = await fetch(`/api/admin/formadores/${formadorId}/docs/${tipo}`, {
        method: "DELETE", credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success("Documento removido.");
      onChange({ [tipo]: null });
    } catch (e) {
      toast.error(`Erro: ${e.message || e}`);
    } finally { setBusy(null); }
  };

  return (
    <div className="pt-3 border-t border-[#F3F0E7]" data-testid="formador-docs-section">
      <p className="text-[11px] uppercase tracking-wider text-[#978D78] font-bold mb-2">
        <FileText className="inline w-3 h-3 mr-1" /> Documentos do Formador
      </p>
      <div className="space-y-2">
        {DOC_TYPES.map(({ key, label, hint }) => {
          const doc = docs?.[key];
          const isBusy = busy === key;
          return (
            <div key={key}
                 className="flex items-center gap-2 text-xs p-2 border border-[#E6E1D6] rounded bg-white"
                 data-testid={`formador-doc-${key}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#1B1B1B]">{label}</div>
                <div className="text-[10px] text-[#6E6754]">
                  {doc ? (
                    <span className="inline-flex items-center gap-1">
                      <CheckCircle2 className="w-2.5 h-2.5 text-[#1F7A3B]" />
                      Upload:{" "}
                      <span className="font-semibold text-[#1F7A3B]" data-testid={`formador-doc-date-${key}`}>
                        {new Date(doc.uploaded_em).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" })}
                      </span>
                      <span className="text-[#978D78]">
                        · {new Date(doc.uploaded_em).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </span>
                  ) : hint}
                </div>
              </div>
              {doc ? (
                <>
                  <a href={doc.url} target="_blank" rel="noopener noreferrer"
                     className="text-[10px] px-2 py-1 rounded bg-[#E7F5EB] text-[#1F7A3B] hover:bg-[#1F7A3B] hover:text-white"
                     data-testid={`formador-doc-link-${key}`}>
                    <CheckCircle2 className="inline w-3 h-3 mr-0.5" /> Ver PDF
                  </a>
                  <button type="button" onClick={() => remove(key)} disabled={isBusy}
                          className="text-[10px] px-2 py-1 text-red-600 hover:bg-red-50 rounded"
                          data-testid={`formador-doc-remove-${key}`}>
                    {isBusy ? <Loader2 className="inline w-3 h-3 animate-spin" /> : <X className="inline w-3 h-3" />}
                  </button>
                </>
              ) : (
                <label className="cursor-pointer text-[10px] px-2 py-1 rounded border border-[#A8843C] text-[#A8843C] hover:bg-[#A8843C] hover:text-white inline-flex items-center gap-1"
                       data-testid={`formador-doc-upload-label-${key}`}>
                  {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                  {isBusy ? "A carregar…" : "Upload PDF"}
                  <input type="file" accept="application/pdf" className="hidden"
                         disabled={isBusy}
                         onChange={(e) => {
                           const f = e.target.files?.[0]; e.target.value = "";
                           if (f) upload(key, f);
                         }}
                         data-testid={`formador-doc-upload-${key}`} />
                </label>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
